import { Autocomplete, Box, SxProps, TextField, Theme, Typography } from '@mui/material'
import { FC } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { BRAND_CORAL, BRAND_CORAL_WASH } from '../constants'
import { COUNTRIES, Country, findCountryByDial } from '../constants/countries'
import { PhoneValue, VendorOnboardingForm } from '../interfaces'
import { digitsOnly, validatePhoneNumber } from '../validators'

interface PhoneFieldProps {
    /** RHF path to a PhoneValue, e.g. 'billing.phone' or 'contacts.0.phone'. */
    name: string
    label: string
    required?: boolean
    /** Forwarded to the root, so the parent grid can make this span a full row. */
    sx?: SxProps<Theme>
}

/**
 * Country code and number are captured separately — a searchable dial-code picker
 * joined to a digits-only number field — and combined only on the wire
 * (`+91 9876543210`), which keeps n8n's flat 'Billing Phone' contract unchanged.
 *
 * The label floats inside the number field rather than sitting above the control:
 * every other field on the page does the same, and an external label would push
 * this input lower than its grid neighbours.
 */
const PhoneField: FC<PhoneFieldProps> = ({ name, label, required = false, sx }) => {
    const { control } = useFormContext<VendorOnboardingForm>()

    return (
        <Controller
            control={control}
            name={name as never}
            rules={{
                validate: (value) => {
                    const phone = (value ?? {}) as PhoneValue
                    return validatePhoneNumber(
                        phone.number ?? '',
                        phone.dial ?? '+91',
                        required
                    )
                },
            }}
            render={({ field, fieldState }) => {
                const value = (field.value ?? {}) as PhoneValue
                const selected = findCountryByDial(value.dial)
                const hasError = Boolean(fieldState.error)

                return (
                    <Box sx={sx}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                            <Autocomplete<Country>
                                options={COUNTRIES}
                                value={selected}
                                disableClearable={false}
                                openOnFocus
                                // After a pick the input holds the dial code (e.g. '+91').
                                // selectOnFocus highlights it so typing a country name
                                // replaces it instead of appending to it.
                                selectOnFocus
                                blurOnSelect
                                handleHomeEndKeys
                                getOptionLabel={(option) => option.dial}
                                isOptionEqualToValue={(option, val) =>
                                    option.code === val.code
                                }
                                filterOptions={(options, state) => {
                                    const query = state.inputValue.trim().toLowerCase()
                                    if (!query) return options
                                    return options.filter(
                                        (option) =>
                                            option.name.toLowerCase().includes(query) ||
                                            option.dial.includes(query) ||
                                            option.code.toLowerCase().startsWith(query)
                                    )
                                }}
                                onChange={(_, next) =>
                                    field.onChange({
                                        ...value,
                                        dial: next?.dial ?? '+91',
                                    })
                                }
                                renderOption={(props, option) => (
                                    <Box
                                        component="li"
                                        {...props}
                                        key={`${option.code}-${option.dial}`}
                                        sx={{ display: 'flex', gap: 1.5 }}
                                    >
                                        <Typography
                                            component="span"
                                            sx={{
                                                color: BRAND_CORAL,
                                                fontWeight: 600,
                                                minWidth: 52,
                                            }}
                                        >
                                            {option.dial}
                                        </Typography>
                                        <Typography
                                            component="span"
                                            sx={{ color: 'text.primary' }}
                                        >
                                            {option.name}
                                        </Typography>
                                    </Box>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="+91"
                                        error={hasError}
                                    />
                                )}
                                sx={{
                                    width: 116,
                                    flexShrink: 0,
                                    '& .MuiOutlinedInput-root': {
                                        borderTopRightRadius: 0,
                                        borderBottomRightRadius: 0,
                                        backgroundColor: '#fff',
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: BRAND_CORAL,
                                            borderWidth: 2,
                                        },
                                    },
                                    // Autocomplete adds its own vertical padding on the
                                    // input ROOT, on top of the theme's `paddingBlock:
                                    // .7rem` on the inner input (theme/components.ts,
                                    // MuiFormControl variant). Zero the root padding and
                                    // restore the same token on the input, so the height
                                    // matches every other field and tracks the theme.
                                    '& .MuiAutocomplete-inputRoot': {
                                        paddingTop: '0 !important',
                                        paddingBottom: '0 !important',
                                    },
                                    '& .MuiAutocomplete-input': {
                                        paddingTop: '.7rem !important',
                                        paddingBottom: '.7rem !important',
                                    },
                                }}
                                slotProps={{ popper: { style: { width: 320 } } }}
                            />

                            <TextField
                                label={`${label}${required ? ' *' : ''}`}
                                value={value.number ?? ''}
                                onChange={(event) =>
                                    field.onChange({
                                        ...value,
                                        number: digitsOnly(event.target.value),
                                    })
                                }
                                onBlur={field.onBlur}
                                inputMode="numeric"
                                error={hasError}
                                helperText={
                                    fieldState.error?.message ?? '10-digit number'
                                }
                                fullWidth
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderTopLeftRadius: 0,
                                        borderBottomLeftRadius: 0,
                                        // Overlap by the border width so the two controls
                                        // read as one joined field.
                                        marginLeft: '-1px',
                                        backgroundColor: '#fff',
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: BRAND_CORAL,
                                            borderWidth: 2,
                                        },
                                        '&.Mui-focused': {
                                            boxShadow: `0 0 0 4px ${BRAND_CORAL_WASH}`,
                                        },
                                    },
                                    '& label.Mui-focused': { color: BRAND_CORAL },
                                }}
                            />
                        </Box>
                    </Box>
                )
            }}
        />
    )
}

export default PhoneField
